import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

interface UserMenuProps {
  onOpenAuthModal: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ onOpenAuthModal }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Handle logout with navigation
  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // If no user, show login button
  if (!user) {
    return (
      <button
        onClick={onOpenAuthModal}
        className="py-2 px-4 bg-[#FF4D8C] hover:bg-[#FF6E9E] text-white rounded-xl transition-colors"
      >
        Log In
      </button>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 focus:outline-none"
      >
        <div className="w-9 h-9 bg-[#FF4D8C] rounded-full flex items-center justify-center text-white font-medium">
          {user.displayName.charAt(0).toUpperCase()}
        </div>
        <span className="hidden md:block text-white">{user.displayName}</span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 py-2 bg-[#121216] border border-gray-800 rounded-xl shadow-xl z-10">
          <div className="px-4 py-2 border-b border-gray-800">
            <p className="text-white font-medium">{user.displayName}</p>
            <p className="text-gray-400 text-sm truncate">{user.email}</p>
          </div>
          
          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors"
          >
            Log Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
